<div class="boost-wrapper" [style.--boost-bg-image]="backgroundImage ? 'url(' + backgroundImage + ')' : 'none'">
  <div class="boost-grid">

    <!-- LEFT: Form -->
    <div class="boost-form-column">

      <div class="boost-title-section">
        <h1 class="boost-title">{{ 'subscriptions.public.title' | transloco }}</h1>
        <p class="subtitle">{{ 'subscriptions.public.subtitle' | transloco }}</p>
      </div>

      <form [formGroup]="subscriptionForm" (ngSubmit)="onSubmit()" class="boost-form">
        <!-- Fields: a white card on mobile, transparent (display: contents) on desktop -->
        <div class="boost-fields">

        <!-- Platform Pills and Service Selection -->
        <app-product-picker
          [texts]="{
            platform: 'subscriptions.public.platform' | transloco,
            loadPlatformsError: 'subscriptions.public.loadPlatformsError' | transloco,
            noPlatforms: 'subscriptions.public.noPlatforms' | transloco,
            service: 'subscriptions.public.service' | transloco,
            loadProductsError: 'subscriptions.public.loadProductsError' | transloco,
            noProducts: 'subscriptions.public.noProducts' | transloco,
            searchService: 'subscriptions.public.searchService' | transloco,
            selectService: 'subscriptions.public.selectService' | transloco
          }">
        </app-product-picker>

        <!-- Preset Plans -->
        @if (selectedProduct) {
          <div class="form-group">
            <label class="form-label">{{ 'subscriptions.public.subscriptionPlan' | transloco }}</label>
            <div class="preset-cards">
              @for (preset of presets; track preset.id) {
                <button
                  type="button"
                  class="preset-card"
                  [class.active]="selectedPreset?.id === preset.id"
                  (click)="selectPreset(preset)">
                  @if (preset.id === 'custom') {
                    <span class="preset-name">{{ 'subscriptions.public.custom' | transloco }}</span>
                    <span class="preset-desc">{{ 'subscriptions.public.customDesc' | transloco }}</span>
                  } @else {
                    <span class="preset-name">{{ preset.runs }} {{ 'subscriptions.public.deliveries' | transloco }}</span>
                    <span class="preset-desc">{{ formatInterval(preset.intervalMinutes) }}</span>
                  }
                </button>
              }
            </div>
          </div>

          <!-- Custom fields -->
          @if (selectedPreset?.id === 'custom') {
            <div class="locked-row locked-row--pair">
              <div class="form-group">
                <label class="form-label">{{ 'subscriptions.public.numDeliveries' | transloco }}</label>
                <app-input
                  formControlName="totalRuns"
                  type="number"
                  size="medium"
                  placeholder="30"
                  [error]="isFieldInvalid('totalRuns')"
                  [errorMessage]="getFieldError('totalRuns')">
                </app-input>
              </div>
              <div class="form-group">
                <label class="form-label">{{ 'subscriptions.public.intervalMinutes' | transloco }}</label>
                <app-input
                  formControlName="intervalMinutes"
                  type="number"
                  size="medium"
                  placeholder="1440"
                  [error]="isFieldInvalid('intervalMinutes')"
                  [errorMessage]="getFieldError('intervalMinutes')">
                </app-input>
              </div>
            </div>
          }
        }

        <!-- Link and Quantity Row -->
        @if (selectedPreset) {
          <div class="locked-row">
            <div class="form-group">
              <label class="form-label">{{ 'subscriptions.public.link' | transloco }}</label>
              <app-input
                formControlName="link"
                type="text"
                size="medium"
                [placeholder]="linkPlaceholder"
                [error]="isFieldInvalid('link')"
                [errorMessage]="getFieldError('link')">
              </app-input>
            </div>
            <div class="form-group">
              <label class="form-label">{{ 'subscriptions.public.amountPerDelivery' | transloco }}</label>
              <app-input
                formControlName="amountPerRun"
                type="number"
                size="medium"
                [placeholder]="selectedProductDetails ? ('boost.form.quantityRange' | transloco:{ min: ((selectedProductDetails.minAmount || 1) | appNumber), max: selectedProductDetails.maxAmount ? (selectedProductDetails.maxAmount | appNumber) : '∞' }) : ('boost.form.quantityPlaceholder' | transloco)"
                [error]="isFieldInvalid('amountPerRun')"
                [errorMessage]="getFieldError('amountPerRun')">
              </app-input>
            </div>
          </div>
        }
        </div>

        @if (selectedPreset) {
          <!-- CTA and Price Row -->
          <div class="cta-row">
            <app-button
              [label]="creatingSubscription ? ('subscriptions.public.processing' | transloco) : ('subscriptions.public.subscribe' | transloco)"
              variant="primary"
              size="medium"
              type="submit"
              [disabled]="creatingSubscription"
              class="submit-button">
            </app-button>

            <div class="price-area">
              <span class="price-label">{{ 'subscriptions.public.total' | transloco }}</span>
              @if (totalPrice > 0) {
                <span class="price-value">{{ finalPrice | appCurrency }}</span>
              } @else if (priceError) {
                <span class="price-value" role="alert">{{ 'common.states.priceError' | transloco }}</span>
                <app-button [label]="'common.actions.retry' | transloco" variant="secondary" size="small" (onClick)="retryPrice()"></app-button>
              } @else {
                <span class="price-value">-- €</span>
              }
            </div>
          </div>
        }

      </form>
    </div>

    <!-- RIGHT: Summary -->
    <div class="boost-summary-column">
      @if (selectedPreset && amountPerRun > 0 && totalRuns > 0) {
        <div class="order-summary-panel">
          <div class="osp-header">
            <div class="osp-icon">
              <lucide-icon name="RefreshCw" class="icon-em"></lucide-icon>
            </div>
            <div class="osp-header-text">
              <span class="osp-title">{{ 'subscriptions.public.summary.title' | transloco }}</span>
              <span class="osp-subtitle">{{ 'subscriptions.public.summary.subtitle' | transloco }}</span>
            </div>
          </div>

          <div class="osp-lines">
            <div class="osp-line">
              <span class="osp-line-label">{{ 'subscriptions.public.summary.service' | transloco }}</span>
              <span class="osp-line-value">{{ selectedProductName }}</span>
            </div>
            <div class="osp-line">
              <span class="osp-line-label">{{ 'subscriptions.public.summary.amountPerDelivery' | transloco }}</span>
              <span class="osp-line-value">{{ amountPerRun | appNumber }}</span>
            </div>
            <div class="osp-line">
              <span class="osp-line-label">{{ 'subscriptions.public.summary.deliveries' | transloco }}</span>
              <span class="osp-line-value">{{ totalRuns }}</span>
            </div>
            <div class="osp-line">
              <span class="osp-line-label">{{ 'subscriptions.public.summary.frequency' | transloco }}</span>
              <span class="osp-line-value">{{ intervalLabel }}</span>
            </div>
            <div class="osp-line">
              <span class="osp-line-label">{{ 'subscriptions.public.summary.totalUnits' | transloco }}</span>
              <span class="osp-line-value">{{ totalAmount | appNumber }}</span>
            </div>
          </div>

          @if (pricePerRun > 0) {
            <div class="osp-lines">
              <div class="osp-line">
                <span class="osp-line-label">{{ 'subscriptions.public.summary.pricePerDelivery' | transloco }}</span>
                <span class="osp-line-value">{{ pricePerRun | appCurrency }}</span>
              </div>
            </div>
          }

          @if (discount) {
            <div class="osp-discount-section">
              <div class="osp-discount-line">
                <span class="osp-discount-label">{{ 'boost.summary.subtotal' | transloco }}</span>
                <span class="osp-subtotal-value">{{ discount.originalPrice | appCurrency }}</span>
              </div>
              <div class="osp-discount-line">
                <span class="osp-discount-name">{{ discount.discountName || ('boost.summary.discount' | transloco) }}{{ discount.discountType === 'PERCENTAGE' ? ' (-' + discount.discountValue + '%)' : '' }}</span>
                <span class="osp-discount-amount">-{{ discount.discountAmount | appCurrency }}</span>
              </div>
            </div>
          }

          <div class="osp-total">
            <span class="osp-total-label">{{ 'subscriptions.public.summary.total' | transloco }}</span>
            <!-- Same rule as the total under the form: no price yet shows '--', not 0,00 € -->
            @if (totalPrice > 0) {
              <span class="osp-total-amount">{{ finalPrice | appCurrency }}</span>
            } @else {
              <span class="osp-total-amount">-- €</span>
            }
          </div>
        </div>
      } @else {
        <div class="empty-summary">
          <div class="empty-icon">
            <lucide-icon name="RefreshCw" class="icon-em"></lucide-icon>
          </div>
          <h3 class="empty-title">{{ 'subscriptions.public.empty.title' | transloco }}</h3>
          <p class="empty-subtitle">{{ 'subscriptions.public.empty.subtitle' | transloco }}</p>
        </div>
      }
    </div>

  </div>

  <!-- Payment Modal -->
  <app-payment-method-modal
    [allowCrypto]="false"
    [(visible)]="showPaymentModal"
    [totalAmount]="finalPrice"
    [originalAmount]="discount ? discount.originalPrice : 0"
    [discountName]="discount?.discountName || ''"
    [productName]="selectedProductName"
    [quantity]="totalAmount"
    [orderId]="pendingSubscriptionId"
    [isCreatingOrder]="isCreatingManualSubscription"
    (paymentMethodSelected)="onPaymentMethodSelected($event)"
    (manualPaymentSelected)="onManualPaymentSelected($event)"
    (manualPaymentConfirmed)="onManualPaymentConfirmed($event)"
    (cancelled)="onPaymentModalCancelled()">
  </app-payment-method-modal>
</div>
